import React, { useState, useMemo } from 'react';
import { Button, Col, Row, Label, Alert } from 'reactstrap';
import Select from 'react-select';
import { separarNombres } from '../../helpers/helpersFacturacion';

/* ===================== Helpers seguros ===================== */
function safeGet() {
  for (var i = 0; i < arguments.length; i++) {
    var v = arguments[i];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return '';
}
function toInt(v, defVal) {
  var n = parseInt(v, 10);
  return isNaN(n) ? defVal : n;
}
function calcularEdadDesdeFecha(fechaISO) {
  if (!fechaISO || typeof fechaISO !== 'string') return null;
  var parts = fechaISO.split('-');
  if (parts.length !== 3) return null;
  var y = parseInt(parts[0], 10), m = parseInt(parts[1], 10) - 1, d = parseInt(parts[2], 10);
  if (isNaN(y) || isNaN(m) || isNaN(d)) return null;
  var hoy = new Date();
  var nac = new Date(y, m, d);
  var edad = hoy.getFullYear() - nac.getFullYear();
  var mes = hoy.getMonth() - nac.getMonth();
  if (mes < 0 || (mes === 0 && hoy.getDate() < nac.getDate())) {
    edad--;
  }
  return edad;
}

/* ===================== Validaciones de identificación (EC) ===================== */
// Cédula (10 dígitos, algoritmo módulo 10)
function validarCedulaEc(ced) {
  if (typeof ced !== 'string') return false;
  var c = ced.replace(/\D/g, '');
  if (c.length !== 10) return false;

  var prov = parseInt(c.substr(0, 2), 10);
  if (isNaN(prov) || prov < 1 || prov > 24) return false;

  var tercer = parseInt(c.charAt(2), 10);
  if (isNaN(tercer) || tercer > 5) return false;

  var coef = [2, 1, 2, 1, 2, 1, 2, 1, 2];
  var suma = 0;
  for (var i = 0; i < 9; i++) {
    var dig = parseInt(c.charAt(i), 10);
    if (isNaN(dig)) return false;
    var prod = dig * coef[i];
    if (prod >= 10) prod -= 9;
    suma += prod;
  }
  var decena = Math.ceil(suma / 10) * 10;
  var verificador = decena - suma;
  if (verificador === 10) verificador = 0;

  return verificador === parseInt(c.charAt(9), 10);
}

// RUC (13 dígitos). Reglas para natural (<6), público (=6), sociedad (=9)
function validarRucEc(ruc) {
  if (typeof ruc !== 'string') return false;
  var r = ruc.replace(/\D/g, '');
  if (r.length !== 13) return false;

  var prov = parseInt(r.substr(0, 2), 10);
  if (isNaN(prov) || prov < 1 || prov > 24) return false;

  var tercer = parseInt(r.charAt(2), 10);
  if (isNaN(tercer)) return false;

  // Últimos 3 dígitos deben ser >= 001
  var suf = parseInt(r.substr(10, 3), 10);
  if (isNaN(suf) || suf < 1) return false;

  if (tercer < 6) {
    // Persona natural: primeros 10 valen como cédula
    var cedulaDiez = r.substr(0, 10);
    if (!validarCedulaEc(cedulaDiez)) return false;
    return true;
  } else if (tercer === 6) {
    // Público: coef 3,2,7,6,5,4,3,2 (pos 0..7), dígito verificador en pos 8 (módulo 11)
    var coef = [3, 2, 7, 6, 5, 4, 3, 2];
    var suma = 0;
    for (var i = 0; i < coef.length; i++) {
      var n = parseInt(r.charAt(i), 10);
      if (isNaN(n)) return false;
      suma += n * coef[i];
    }
    var dvCalc = 11 - (suma % 11);
    if (dvCalc === 11) dvCalc = 0;
    if (dvCalc === 10) return false;
    var dv = parseInt(r.charAt(8), 10);
    if (isNaN(dv) || dv !== dvCalc) return false;
    return true;
  } else if (tercer === 9) {
    // Sociedades privadas/Extranjeras: coef 4,3,2,7,6,5,4,3,2 (pos 0..8), verificador pos 9 (módulo 11)
    var coef9 = [4, 3, 2, 7, 6, 5, 4, 3, 2];
    var suma9 = 0;
    for (var j = 0; j < coef9.length; j++) {
      var n2 = parseInt(r.charAt(j), 10);
      if (isNaN(n2)) return false;
      suma9 += n2 * coef9[j];
    }
    var dvCalc9 = 11 - (suma9 % 11);
    if (dvCalc9 === 11) dvCalc9 = 0;
    if (dvCalc9 === 10) return false;
    var dv9 = parseInt(r.charAt(9), 10);
    if (isNaN(dv9) || dv9 !== dvCalc9) return false;
    return true;
  }
  return false;
}

// Pasaporte: regla flexible (alfa-numérico 6-15)
function validarPasaporte(p) {
  if (typeof p !== 'string') return false;
  var s = p.trim();
  if (!/^[A-Za-z0-9]{6,15}$/.test(s)) return false;
  return true;
}

/* ===================== Componente ===================== */
const FormularioAsegurado = ({ planesOptions, contratantesOptions, onSubmit, defaultValues, modoEdicion }) => {
  // Opciones para Select (plan / contratante)
  const planOptions = useMemo(function () {
    return (Array.isArray(planesOptions) ? planesOptions : []).map(function (p) {
      return { value: String(safeGet(p.codigo, p.value, p._id)), label: String(safeGet(p.nombre, p.label, safeGet(p.codigo, p.value, p._id))), edadMinima: p && p.edadMinima, edadMaxima: p && p.edadMaxima };
    });
  }, [planesOptions]);

  const contratOptions = useMemo(function () {
    return (Array.isArray(contratantesOptions) ? contratantesOptions : []).map(function (c) {
      return { value: String(safeGet(c.codigo, c.value, c._id)), label: String(safeGet(c.nombreCompleto, c.label, safeGet(c.codigo, c.value, c._id))) };
    });
  }, [contratantesOptions]);

  // Selects locales
  var tipoDocOptions = [
    { value: 'CED', label: 'Cédula' },
    { value: 'PAS', label: 'Pasaporte' },
    { value: 'RUC', label: 'RUC' }
  ];
  var generoOptions = [
    { value: 'M', label: 'Masculino' },
    { value: 'F', label: 'Femenino' }
  ];

  // Default asegurado
  var base = {
    plan: null,
    contratante: null,
    nombre: '',
    tipo_documento: null,     // {value,label}
    identificacion: '',
    email: '',
    celular: '',
    nombres: '',
    apellidos: '',
    genero: null,             // {value,label}
    fecha_nacimiento: ''      // YYYY-MM-DD
  };

  if (defaultValues && typeof defaultValues === 'object') {
    base.plan = defaultValues.plan || null;
    base.contratante = defaultValues.contratante || null;
    base.nombre = defaultValues.nombre || '';
    base.nombres = defaultValues.nombres || '';
    base.apellidos = defaultValues.apellidos || '';
    base.identificacion = defaultValues.identificacion || '';
    base.email = defaultValues.email || '';
    base.celular = defaultValues.celular || '';
    base.fecha_nacimiento = defaultValues.fecha_nacimiento || '';
    // Mapeos si vinieran como string:
    if (defaultValues.tipo_documento) {
      var td = String(defaultValues.tipo_documento).toUpperCase();
      for (var i = 0; i < tipoDocOptions.length; i++) {
        if (tipoDocOptions[i].value === td) { base.tipo_documento = tipoDocOptions[i]; break; }
      }
    }
    if (defaultValues.genero) {
      var g = String(defaultValues.genero).toUpperCase();
      for (var j = 0; j < generoOptions.length; j++) {
        if (generoOptions[j].value === g) { base.genero = generoOptions[j]; break; }
      }
    }
  }

  const [asegurado, setAsegurado] = useState({ ...base });
  const [errors, setErrors] = useState({});
  const [showAlert, setShowAlert] = useState(false);

  function handleChange(field, value) {
    setAsegurado(function (prev) {
      var next = { ...prev, [field]: value };
      // Si cambia tipo de documento, limpiamos la identificación
      if (field === 'tipo_documento') {
        next.identificacion = '';
      }
      return next;
    });
  }

  function findPlanByValue(value) {
    if (!Array.isArray(planesOptions)) return null;
    var val = String(value);
    for (var i = 0; i < planesOptions.length; i++) {
      var p = planesOptions[i];
      var pCode = String(safeGet(p.codigo, p.value, p._id));
      if (pCode === val) return p;
    }
    return null;
  }

  function validarIdentificacionPorTipo(tipoSel, id) {
    if (!tipoSel || !tipoSel.value) return 'Seleccione el tipo de documento';
    var t = String(tipoSel.value).toUpperCase();
    if (!id || String(id).trim() === '') return 'Identificación requerida';

    if (t === 'CED') {
      if (!/^\d{10}$/.test(id)) return 'La cédula debe tener 10 dígitos';
      if (!validarCedulaEc(id)) return 'Cédula inválida';
      return '';
    }
    if (t === 'RUC') {
      if (!/^\d{13}$/.test(id)) return 'El RUC debe tener 13 dígitos';
      if (!validarRucEc(id)) return 'RUC inválido';
      return '';
    }
    if (t === 'PAS') {
      if (!validarPasaporte(id)) return 'Pasaporte inválido (6-15 caracteres alfanuméricos)';
      return '';
    }
    return 'Tipo de documento no soportado';
  }

  function validar() {
    var err = {};

    // Plan
    if (!asegurado.plan || !asegurado.plan.value) {
      err.plan = 'Seleccione un plan';
    }

    // Contratante
    if (!asegurado.contratante || !asegurado.contratante.value) {
      err.contratante = 'Seleccione un contratante';
    }

    // Nombres
    if (!asegurado.nombres) {
      err.nombres = 'Campo requerido';
    } else if (/[^a-zA-ZáéíóúüñÁÉÍÓÚÜÑ\s]/.test(asegurado.nombres)) {
      err.nombres = 'Permite únicamente el ingreso de letras y espacios';
    }

    // Apellidos
    if (!asegurado.apellidos) {
      err.apellidos = 'Campo requerido';
    } else if (/[^a-zA-ZáéíóúüñÁÉÍÓÚÜÑ\s]/.test(asegurado.apellidos)) {
      err.apellidos = 'Permite únicamente el ingreso de letras y espacios';
    }

    //Email
    if (!asegurado.email) {
      err.email = 'Campo requerido';
    } else {
      // Validación básica de formato de email
      var emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(asegurado.email)) {
        err.email = 'Email inválido';
      }
    }

    // Celular
    if (!asegurado.celular) {
      err.celular = 'Campo requerido';
    } else {
      // Validación básica de formato de celular (10 dígitos)
      var celularRegex = /^\d{10}$/;
      if (!celularRegex.test(asegurado.celular)) {
        err.celular = 'Número de celular inválido (debe tener 10 dígitos)';
      }
    }


    // Tipo doc + Identificación
    var errId = validarIdentificacionPorTipo(asegurado.tipo_documento, asegurado.identificacion);
    if (errId) err.identificacion = errId;

    // Género
    if (!asegurado.genero || !asegurado.genero.value) {
      err.genero = 'Seleccione el género';
    }

    // Fecha de nacimiento
    if (!asegurado.fecha_nacimiento) {
      err.fecha_nacimiento = 'La fecha de nacimiento es obligatoria';
    } else {
      var edad = calcularEdadDesdeFecha(asegurado.fecha_nacimiento);
      if (edad === null) {
        err.fecha_nacimiento = 'Fecha de nacimiento inválida';
      } else {
        var planSelVal = asegurado.plan && asegurado.plan.value ? asegurado.plan.value : '';
        var planSel = findPlanByValue(planSelVal);
        var min = toInt(planSel && planSel.edadMinima, 1);
        var max = toInt(planSel && planSel.edadMaxima, 99);
        if (edad < min || edad > max) {
          err.fecha_nacimiento = 'La edad del asegurado debe estar entre ' + min + ' y ' + max + ' años para el plan seleccionado.';
        }
      }
    }

    setErrors(err);
    setShowAlert(Object.keys(err).length > 0);
    return Object.keys(err).length === 0;
  }

  function handleGuardar() {
    if (!validar()) return;
    setShowAlert(false);

    const [primerNombre, segundoNombre] = separarNombres(asegurado.nombres);
    const [primerApellido, segundoApellido] = separarNombres(asegurado.apellidos);

    const nombre = [primerNombre, segundoNombre, primerApellido, segundoApellido].filter(function (n) { return n && n.trim() !== ''; }).join(' ');

    var datos = {
      plan: asegurado.plan && asegurado.plan.value,
      contratante: asegurado.contratante && asegurado.contratante.value,
      nombre: nombre,
      primerNombre: primerNombre,
      segundoNombre: segundoNombre,
      primerApellido: primerApellido,
      segundoApellido: segundoApellido,
      tipo_documento: asegurado.tipo_documento && asegurado.tipo_documento.value,
      identificacion: asegurado.identificacion,
      email: asegurado.email,
      celular: asegurado.celular,
      genero: asegurado.genero && asegurado.genero.value,
      fecha_nacimiento: asegurado.fecha_nacimiento
    };
    onSubmit(datos);
  }

  var selectStyles = {
    container: function (base) { return { ...base, width: '100%' }; },
    control: function (base) { return { ...base, borderColor: '#ced4da', minHeight: '38px', fontSize: '14px' }; },
    singleValue: function (base) { return { ...base, color: '#495057' }; }
  };

  var inputStyle = {
    border: '1px solid #ced4da',
    borderRadius: '4px',
    minHeight: '38px',
    fontSize: '14px',
    padding: '5px 10px',
    width: '100%',
    color: '#495057'
  };

  return (
    <>
      {showAlert && (
        <Alert color="danger" toggle={function () { setShowAlert(false); }}>
          Hay errores en el formulario. Revisa los campos marcados en rojo.
        </Alert>
      )}

      <Row>
        <Col md="12">
          <Label style={{ fontWeight: 'bold' }}>Plan</Label>
          <Select
            value={asegurado.plan}
            options={planOptions}
            placeholder="Seleccione un plan de cobertura"
            onChange={function (val) { handleChange('plan', val); }}
            styles={selectStyles}
          />
          {errors.plan && <small style={{ color: 'red' }}>{errors.plan}</small>}
        </Col>

        <Col md="12">
          <Label style={{ fontWeight: 'bold' }}>Pagador</Label>
          <Select
            value={asegurado.contratante}
            options={contratOptions}
            placeholder="Seleccione quién realizará el pago"
            onChange={function (val) { handleChange('contratante', val); }}
            styles={selectStyles}
          />
          {errors.contratante && <small style={{ color: 'red' }}>{errors.contratante}</small>}
        </Col>



        {/* Tipo de documento */}
        <Col md="12">
          <Label style={{ fontWeight: 'bold' }}>Tipo de documento</Label>
          <Select
            value={asegurado.tipo_documento}
            options={tipoDocOptions}
            onChange={function (val) { handleChange('tipo_documento', val); }}
            styles={selectStyles}
            placeholder="Seleccione tipo de documento"
            isClearable
          />
        </Col>

        {/* Identificación, validada según el tipo */}
        <Col md="12">
          <Label style={{ fontWeight: 'bold' }}>Identificación</Label>
          <input
            type="text"
            style={inputStyle}
            value={asegurado.identificacion}
            onChange={function (e) { handleChange('identificacion', e.target.value); }}
            placeholder={(asegurado.tipo_documento && asegurado.tipo_documento.value === 'CED') ? '10 dígitos'
              : (asegurado.tipo_documento && asegurado.tipo_documento.value === 'RUC') ? '13 dígitos'
                : (asegurado.tipo_documento && asegurado.tipo_documento.value === 'PAS') ? '6-15 caracteres alfanuméricos'
                  : 'Ingrese identificación'}
          />
          {errors.identificacion && <small style={{ color: 'red' }}>{errors.identificacion}</small>}
        </Col>
        {/* Nombres */}
        <Col md="12">
          <Label style={{ fontWeight: 'bold' }}>Nombres</Label>
          <input
            type="text"
            style={inputStyle}
            value={asegurado.nombres}
            onChange={function (e) {
              var val = e.target.value.replace(/[^a-zA-ZáéíóúüñÁÉÍÓÚÜÑ\s]/g, '');
              handleChange('nombres', val);
            }}
          />
          {errors.nombres && <small style={{ color: 'red' }}>{errors.nombres}</small>}
        </Col>
        <Col md="12">
          <Label style={{ fontWeight: 'bold' }}>Apellidos</Label>
          <input
            type="text"
            style={inputStyle}
            value={asegurado.apellidos}
            onChange={function (e) {
              var val = e.target.value.replace(/[^a-zA-ZáéíóúüñÁÉÍÓÚÜÑ\s]/g, '');
              handleChange('apellidos', val);
            }}
          />
          {errors.apellidos && <small style={{ color: 'red' }}>{errors.apellidos}</small>}
        </Col>
        {/* Email */}
        <Col md="12">
          <Label style={{ fontWeight: 'bold' }}>Email</Label>
          <input
            type="email"
            style={inputStyle}
            value={asegurado.email}
            onChange={function (e) { handleChange('email', e.target.value); }}
          />
          {errors.email && <small style={{ color: 'red' }}>{errors.email}</small>}
        </Col>
        {/* Celular */}
        <Col md="12">
          <Label style={{ fontWeight: 'bold' }}>Celular</Label>
          <input
            type="text"
            style={inputStyle}
            value={asegurado.celular}
            onChange={function (e) { handleChange('celular', e.target.value); }}
          />
          {errors.celular && <small style={{ color: 'red' }}>{errors.celular}</small>}
        </Col>
        {/* Género */}
        <Col md="12">
          <Label style={{ fontWeight: 'bold' }}>Género</Label>
          <Select
            value={asegurado.genero}
            options={generoOptions}
            onChange={function (val) { handleChange('genero', val); }}
            styles={selectStyles}
            placeholder="Seleccione género"
            isClearable
          />
          {errors.genero && <small style={{ color: 'red' }}>{errors.genero}</small>}
        </Col>

        <Col md="12">
          <Label style={{ fontWeight: 'bold' }}>Fecha de nacimiento</Label>
          <input
            type="date"
            style={inputStyle}
            value={asegurado.fecha_nacimiento}
            onChange={function (e) { handleChange('fecha_nacimiento', e.target.value); }}
          />
          {errors.fecha_nacimiento && <small style={{ color: 'red' }}>{errors.fecha_nacimiento}</small>}
        </Col>
      </Row>

      <div className="text-end mt-3">
        <Button
          style={{ backgroundColor: "#001871", color: "white", border: "none" }}
          onClick={handleGuardar}
        >
          <i className="fa fa-save" /> {modoEdicion ? 'Actualizar Asegurado' : 'Guardar Asegurado'}
        </Button>
      </div>
    </>
  );
};

export default FormularioAsegurado;
